import {
  createKeyInitiativeOutcomeItem,
  isKeyInitiativeOutcomeValueType,
  normalizeKeyInitiativeOutcomeItemValue,
  parseKeyInitiativeOutcome,
  serializeKeyInitiativeOutcome,
} from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditorModal/helpers/keyInitiativeOutcome'
import {
  EKeyInitiativeOutcomeValueType,
  IKeyInitiativeOutcomeItem,
} from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/lpKeyInitiatives/components/keyInitiativeEditorModal/models/keyInitiativeOutcomeItem'
import { TRANSLATION_KEYS as tr } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives/translations'
import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { NumberInput } from 'cng-common/components/numberInput'
import { useGetFormDataField } from 'cng-common/features/form'
import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { selectTranslationMode } from 'cng-common/features/translations/selectors'
import { Button } from 'core-components/library/button'
import { Dropdown } from 'core-components/library/dropdown'
import { Input, Textarea } from 'core-components/library/inputs'
import { Tooltip } from 'core-components/library/tooltip'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'

import { IKeyInitiativeOutcomeValueTypeConfig, KEY_INITIATIVE_OUTCOME_VALUE_TYPES } from './config'

import styles from './KeyInitiativeOutcomeSection.module.scss'

const KEYS = tr.keyInitiativeEditor.outcomeFieldGroup

interface IOutcomeItemsEditorProps {
  formKey: string
  initialValue?: string
  isDisabled?: boolean
}

interface IOutcomeValueTypeOption extends IKeyInitiativeOutcomeValueTypeConfig {
  label: string
}

interface IOutcomeItemRowProps {
  item: IKeyInitiativeOutcomeItem
  isDisabled?: boolean
  onChange: (item: IKeyInitiativeOutcomeItem) => void
  onRemove: () => void
}

const OutcomeItemRow = ({ item, isDisabled, onChange, onRemove }: IOutcomeItemRowProps) => {
  const t = useSafetyTranslation()
  const translationMode = useSelector(selectTranslationMode)
  const isTranslationMode = useMemo(() => translationMode === 'edit', [translationMode])

  const valueTypeOptions = useMemo<IOutcomeValueTypeOption[]>(() => {
    return KEY_INITIATIVE_OUTCOME_VALUE_TYPES.map((config) => ({
      ...config,
      label: t(config.labelKey),
    }))
  }, [t])

  const valueTypesSource = useMemo(() => {
    return new Crud<IOutcomeValueTypeOption>({
      fetcher: new Static<IOutcomeValueTypeOption>(valueTypeOptions),
      keyField: 'value',
      defaultPaging: {
        page: 0,
        pageSize: valueTypeOptions.length,
      },
    })
  }, [valueTypeOptions])

  const selectedType = useMemo(() => {
    return valueTypeOptions.find((option) => option.value === item.valueType) ?? valueTypeOptions[0]
  }, [item.valueType, valueTypeOptions])

  const handleTypeChange = useCallback(
    (value: string) => {
      if (!isKeyInitiativeOutcomeValueType(value)) {
        return
      }

      onChange({
        ...item,
        valueType: value,
        value: normalizeKeyInitiativeOutcomeItemValue(item.value, value),
      })
    },
    [item, onChange]
  )

  const handleNumericValueChange = useCallback(
    (value: string) => {
      const numericValue = value === '' ? null : Number(value)
      onChange({
        ...item,
        value: numericValue !== null && Number.isFinite(numericValue) ? numericValue : null,
      })
    },
    [item, onChange]
  )

  const isFreeText = item.valueType === EKeyInitiativeOutcomeValueType.FreeText
  const isPercentage = item.valueType === EKeyInitiativeOutcomeValueType.Percentage
  const maximumFractionDigits = item.valueType === EKeyInitiativeOutcomeValueType.Financial ? 2 : 3

  return (
    <div className={styles.outcomeItemRow}>
      <div className={styles.outcomeFieldControl}>
        {isTranslationMode && <Translation text={KEYS.itemNamePlaceholder} />}

        <Input
          type="text"
          value={item.name}
          placeholder={t(KEYS.itemNamePlaceholder)}
          disabled={isDisabled}
          autocompleteOff
          onChange={(name) => onChange({ ...item, name })}
        />
      </div>

      <div className={styles.outcomeFieldControl}>
        {isTranslationMode && selectedType && <Translation text={selectedType.labelKey} />}

        <Dropdown<IOutcomeValueTypeOption>
          disabled={isDisabled}
          disabledInput
          source={valueTypesSource}
          value={item.valueType}
          caption="label"
          maxListPageSize={valueTypeOptions.length}
          startContent={
            selectedType ? <i className={selectedType.icon} aria-hidden={true} /> : undefined
          }
          renderFunction={(option) => (
            <span className={styles.valueTypeOption}>
              <i className={option.icon} aria-hidden={true} />
              <span>{option.label}</span>
            </span>
          )}
          onChange={handleTypeChange}
        />
      </div>

      <div className={styles.outcomeFieldControl}>
        {isTranslationMode && <Translation text={KEYS.itemValuePlaceholder} />}

        {isFreeText ? (
          <Textarea
            value={typeof item.value === 'string' ? item.value : ''}
            placeholder={t(KEYS.itemValuePlaceholder)}
            disabled={isDisabled}
            autocompleteOff
            rows={3}
            onChange={(value) => onChange({ ...item, value })}
          />
        ) : (
          <NumberInput
            value={typeof item.value === 'number' ? String(item.value) : ''}
            placeholder={t(KEYS.itemValuePlaceholder)}
            disabled={isDisabled}
            min={0}
            max={isPercentage ? 100 : undefined}
            maximumFractionDigits={maximumFractionDigits}
            endContent={
              isPercentage ? <span className={styles.percentageSuffix}>%</span> : undefined
            }
            onChange={handleNumericValueChange}
          />
        )}
      </div>

      <Tooltip
        className={styles.outcomeActionTooltip}
        tipContent={() => <Translation text={KEYS.removeItem} />}
        inline
      >
        <Button
          className={styles.outcomeRemoveButton}
          variant="outlined"
          color="danger"
          iconButton="kbase kbase-minus"
          disabled={isDisabled}
          onClick={onRemove}
        />
      </Tooltip>
    </div>
  )
}

export const OutcomeItemsEditor = ({
  formKey,
  initialValue,
  isDisabled,
}: IOutcomeItemsEditorProps) => {
  const translationMode = useSelector(selectTranslationMode)
  const isTranslationMode = useMemo(() => translationMode === 'edit', [translationMode])
  const [formValue, setFormValue] = useGetFormDataField<string | undefined>(formKey, 'outcome')
  const outcomeItems = useMemo(
    () => parseKeyInitiativeOutcome(formValue === undefined ? initialValue : formValue),
    [formValue, initialValue]
  )

  const updateOutcomeItems = useCallback(
    (items: IKeyInitiativeOutcomeItem[]) => {
      setFormValue(serializeKeyInitiativeOutcome(items))
    },
    [setFormValue]
  )

  const handleAddItem = useCallback(() => {
    updateOutcomeItems([...outcomeItems, createKeyInitiativeOutcomeItem()])
  }, [outcomeItems, updateOutcomeItems])

  const handleChangeItem = useCallback(
    (index: number, item: IKeyInitiativeOutcomeItem) => {
      updateOutcomeItems(
        outcomeItems.map((currentItem, currentIndex) =>
          currentIndex === index ? item : currentItem
        )
      )
    },
    [outcomeItems, updateOutcomeItems]
  )

  const handleRemoveItem = useCallback(
    (index: number) => {
      updateOutcomeItems(outcomeItems.filter((_, currentIndex) => currentIndex !== index))
    },
    [outcomeItems, updateOutcomeItems]
  )

  return (
    <div className={styles.outcomeEditor}>
      <div className={styles.outcomeEditorHeader}>
        <span className={styles.label}>
          <Translation text={KEYS.otherInfo} span />
        </span>
        <Tooltip
          className={styles.outcomeActionTooltip}
          tipContent={() => <Translation text={KEYS.addItem} />}
          inline
        >
          <Button
            className={styles.outcomeAddButton}
            variant="outlined"
            color="primary"
            iconButton="kbase kbase-add"
            disabled={isDisabled}
            onClick={handleAddItem}
          />
        </Tooltip>
        {isTranslationMode && <Translation text={KEYS.addItem} span />}
        {isTranslationMode && <Translation text={KEYS.removeItem} span />}
      </div>

      {outcomeItems.length > 0 && (
        <>
          <div className={styles.outcomeColumnHeaders}>
            <span>
              <Translation text={KEYS.itemName} span />
            </span>
            <span>
              <Translation text={KEYS.itemValueType} span />
            </span>
            <span>
              <Translation text={KEYS.itemValue} span />
            </span>
            <span aria-hidden={true} />
          </div>

          <div className={styles.outcomeItems}>
            {outcomeItems.map((item, index) => (
              <OutcomeItemRow
                key={index}
                item={item}
                isDisabled={isDisabled}
                onChange={(changedItem) => handleChangeItem(index, changedItem)}
                onRemove={() => handleRemoveItem(index)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

OutcomeItemsEditor.displayName = 'OutcomeItemsEditor'
